import { useState, type ReactNode } from 'react';
import { ConfirmDialog, Field, Input } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useSiteOperation } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatDateTime } from '@/utils/format';
import type { SiteBackupView, SiteDetail } from '@/types/api';

/**
 * Restoring a site from one of its backups, in place. Confirmed the way a
 * destroy is — by typing the site's name — because it replaces what the site
 * holds now.
 */
export function RestoreBackupDialog({
  site,
  backup,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly backup: SiteBackupView | null;
  readonly onClose: () => void;
}): ReactNode {
  const [typed, setTyped] = useState('');

  const restore = useSiteOperation(site.id, {
    onSuccess: () => {
      setTyped('');
      toast.success('Восстановление поставлено в очередь');
      onClose();
    },
  });

  return (
    <ConfirmDialog
      open={backup !== null}
      title="Восстановить сайт из копии?"
      description={backup ? `Содержимое сайта будет заменено копией от ${formatDateTime(backup.createdAt)}.` : undefined}
      confirmLabel="Восстановить"
      destructive
      busy={restore.isPending}
      error={restore.isError ? errorText(restore.error, 'Не удалось поставить восстановление в очередь') : undefined}
      onConfirm={() =>
        backup &&
        restore.mutate({
          command: { operation: 'restore', backupId: backup.id, confirmation: typed.trim() },
          key: idempotencyKey('site.restore'),
        })
      }
      onClose={() => {
        setTyped('');
        onClose();
      }}
    >
      <div className="stack">
        <ol className="enroll-steps">
          <li>Создаётся копия текущего состояния.</li>
          <li>Сайт переводится в режим обслуживания.</li>
          <li>База данных и загруженные файлы заменяются содержимым копии.</li>
          <li>Сайт открывается после успешной проверки.</li>
        </ol>
        <Field label="Для подтверждения введите идентификатор сайта" htmlFor="restore-confirm" hint={site.slug}>
          <Input
            id="restore-confirm"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={site.slug}
            autoComplete="off"
          />
        </Field>
      </div>
    </ConfirmDialog>
  );
}
